import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  Group,
  Loader,
  Stack,
  Text,
  Title
} from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  getListUsersQueryKey,
  useDeactivateUser,
  useListUsers,
  useReactivateUser,
  useRevokeUserTokens,
  useSendPasswordReset,
  useUpdateRole
} from '@/api/generated/users/users';
import {
  BackButton,
  CardSection,
  ConfirmActionModal,
  SelectField,
  StatusPill,
  Toast
} from '@/components/common';
import { useSetBreadcrumbExtra } from '@/components/layout/BreadcrumbContext';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { ChangeRoleModal } from '@/features/users/components/ChangeRoleModal';
import { SendResetEmailModal } from '@/features/users/components/SendResetEmailModal';
import { formatDateTime, formatRole } from '@/utils/format';

export const AdminUserDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuthContext();
  const queryClient = useQueryClient();

  // staleTime: 0 — druga sesija može promijeniti status/rolu ovog korisnika
  // bez signala natrag ovom ekranu, isti razlog kao ostale admin liste.
  const { data, isLoading } = useListUsers(undefined, { query: { staleTime: 0 } });
  const user = (data ?? []).find((u) => u.id === id);
  const isSelf = !!user?.id && user.id === currentUser?.id;
  const isAdminRole = !!user?.role && user.role !== 'USER';

  const userDetail = {
    name: user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : '',
    email: user?.email ?? '',
    org: user?.orgName ?? '',
    role: formatRole(user?.role),
    status: user?.isActive ? t('platformAdmin.statusActive') : t('adminUsers.statusDeactivated'),
    lastLogin: formatDateTime(user?.lastLoginAt) ?? t('users.roles.neverLoggedIn')
  };

  useSetBreadcrumbExtra(userDetail.name);
  const [changeRoleOpen, setChangeRoleOpen] = useState(false);
  const [sendResetOpen, setSendResetOpen] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [revokeSessionModalOpen, setRevokeSessionModalOpen] = useState(false);
  const [actionFeedback, setActionFeedback] = useState<{
    tone: 'success' | 'danger';
    messageKey: string;
  } | null>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getListUsersQueryKey(), refetchType: 'all' });

  const updateRoleMutation = useUpdateRole({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        invalidate();
        setChangeRoleOpen(false);
        setActionFeedback({ tone: 'success', messageKey: 'adminUserDetail.roleChangeSuccess' });
      },
      onError: () => {
        setChangeRoleOpen(false);
        setActionFeedback({ tone: 'danger', messageKey: 'adminUserDetail.roleChangeError' });
      }
    }
  });
  const sendResetMutation = useSendPasswordReset({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        setSendResetOpen(false);
        setActionFeedback({
          tone: 'success',
          messageKey: 'adminUserDetail.passwordResetSuccess'
        });
      },
      onError: () => {
        setSendResetOpen(false);
        setActionFeedback({ tone: 'danger', messageKey: 'adminUserDetail.passwordResetError' });
      }
    }
  });
  const deactivateMutation = useDeactivateUser({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        invalidate();
        setDeactivateModalOpen(false);
        setActionFeedback({ tone: 'success', messageKey: 'adminUserDetail.deactivateSuccess' });
      },
      onError: () => {
        setDeactivateModalOpen(false);
        setActionFeedback({ tone: 'danger', messageKey: 'adminUserDetail.deactivateError' });
      }
    }
  });
  const reactivateMutation = useReactivateUser({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        invalidate();
        setDeactivateModalOpen(false);
        setActionFeedback({ tone: 'success', messageKey: 'adminUserDetail.reactivateSuccess' });
      },
      onError: () => {
        setDeactivateModalOpen(false);
        setActionFeedback({ tone: 'danger', messageKey: 'adminUserDetail.reactivateError' });
      }
    }
  });
  const revokeTokensMutation = useRevokeUserTokens({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        setRevokeSessionModalOpen(false);
        setActionFeedback({
          tone: 'success',
          messageKey: 'adminUserDetail.revokeSessionSuccess'
        });
      },
      onError: () => {
        setRevokeSessionModalOpen(false);
        setActionFeedback({ tone: 'danger', messageKey: 'adminUserDetail.revokeSessionError' });
      }
    }
  });

  if (isLoading) {
    return (
      <Center py={80}>
        <Loader color="var(--color-accent-primary)" />
      </Center>
    );
  }

  if (!user) {
    return (
      <Container size="lg" px={0}>
        <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
          {t('users.roles.emptyState')}
        </Text>
      </Container>
    );
  }

  const contextLine = (
    <Flex gap={{ base: 6, sm: 8 }} wrap="nowrap" pt={4}>
      <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
        {userDetail.name}
      </Text>
      <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
        • {userDetail.org}
      </Text>
    </Flex>
  );

  const overviewFields: [string, string][] = [
    [t('adminUserDetail.fieldName'), userDetail.name],
    [t('adminUserDetail.fieldEmail'), userDetail.email],
    [t('adminUserDetail.fieldOrg'), userDetail.org],
    [t('adminUserDetail.roleLabel'), userDetail.role],
    [t('adminUserDetail.fieldStatus'), userDetail.status],
    [t('adminUserDetail.fieldLastLogin'), userDetail.lastLogin]
  ];

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to="/admin/users" />

        <Group justify="space-between" wrap="wrap" gap={12}>
          <Stack gap={8}>
            <Group gap={12} wrap="wrap">
              <Title
                order={2}
                fz={{ base: 20, sm: 24 }}
                style={{ color: 'var(--color-text-primary)' }}
              >
                {userDetail.name}
              </Title>
              <StatusPill
                header
                label={userDetail.status}
                bg={
                  user.isActive
                    ? 'var(--color-status-success-soft)'
                    : 'var(--color-status-danger-soft)'
                }
                fg={user.isActive ? 'var(--color-status-success)' : 'var(--color-status-danger)'}
              />
              <StatusPill
                header
                label={userDetail.role}
                bg={isAdminRole ? 'var(--color-role-client-soft)' : 'var(--color-role-user-soft)'}
                fg={isAdminRole ? 'var(--color-role-client)' : 'var(--color-role-user)'}
              />
            </Group>
            <Text fz={{ base: 13, sm: 14 }} style={{ color: 'var(--color-text-secondary)' }}>
              {userDetail.org}
            </Text>
          </Stack>
        </Group>

        {actionFeedback && (
          <Toast tone={actionFeedback.tone}>
            {t(actionFeedback.messageKey, { name: userDetail.name })}
          </Toast>
        )}

        <CardSection title={t('adminUserDetail.overviewTitle')}>
          <Box style={{ overflowX: 'auto' }} visibleFrom="sm">
            <Group gap={40} p={20} wrap="wrap">
              {overviewFields.map(([label, value]) => (
                <Stack key={label} gap={4} style={{ flex: '1 1 140px' }}>
                  <Text
                    fz={11}
                    fw={600}
                    tt="uppercase"
                    style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                  >
                    {label}
                  </Text>
                  <Text fz={14} fw={500} style={{ color: 'var(--color-text-primary)' }}>
                    {value}
                  </Text>
                </Stack>
              ))}
            </Group>
          </Box>

          <Stack gap={12} p={14} hiddenFrom="sm">
            {overviewFields.map(([label, value], index) => (
              <Group
                key={label}
                justify="space-between"
                wrap="nowrap"
                pb={8}
                style={{
                  borderBottom:
                    index === overviewFields.length - 1
                      ? undefined
                      : '1px solid var(--color-border-soft)'
                }}
              >
                <Text
                  fz={11}
                  fw={600}
                  tt="uppercase"
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {label}
                </Text>
                <Text fz={12} fw={500} style={{ color: 'var(--color-text-primary)' }}>
                  {value}
                </Text>
              </Group>
            ))}
          </Stack>
        </CardSection>

        <Group gap={16} align="stretch" wrap="wrap">
          <Stack
            gap={12}
            p={20}
            style={{
              flex: '1 1 320px',
              minWidth: 280,
              borderRadius: 12,
              border: '1px solid var(--color-border-default)',
              backgroundColor: 'var(--color-bg-surface)'
            }}
          >
            <Group justify="space-between" wrap="wrap" visibleFrom="sm">
              <Text
                fz={{ base: 13.5, sm: 14 }}
                fw={600}
                style={{ color: 'var(--color-text-primary)' }}
              >
                {t('adminUserDetail.roleManagementTitle')}
              </Text>
              <Button
                size="xs"
                disabled={isSelf}
                onClick={() => setChangeRoleOpen(true)}
                styles={{
                  root: {
                    backgroundColor: 'var(--color-border-soft)',
                    color: 'var(--color-text-primary)'
                  }
                }}
              >
                {t('users.roles.menuChangeRole')}
              </Button>
            </Group>
            <Text
              fz={{ base: 13.5, sm: 14 }}
              fw={600}
              style={{ color: 'var(--color-text-primary)' }}
              hiddenFrom="sm"
            >
              {t('adminUserDetail.roleManagementTitle')}
            </Text>
            <Box style={{ width: '100%', maxWidth: 220 }} visibleFrom="sm">
              <SelectField
                label={t('adminUserDetail.roleLabel')}
                data={[userDetail.role]}
                value={userDetail.role}
                disabled
              />
            </Box>
            <Box hiddenFrom="sm" style={{ width: '100%' }}>
              <SelectField hideLabel data={[userDetail.role]} value={userDetail.role} disabled />
            </Box>
            <Text
              fz={{ base: 11.5, sm: 12 }}
              lh={{ base: '16px', sm: '18px' }}
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {isSelf ? t('adminUserDetail.selfActionNote') : t('adminUserDetail.roleHelperText')}
            </Text>
            <Box hiddenFrom="sm">
              <Button
                size="xs"
                fullWidth
                disabled={isSelf}
                onClick={() => setChangeRoleOpen(true)}
                styles={{
                  root: {
                    backgroundColor: 'var(--color-border-soft)',
                    color: 'var(--color-text-primary)'
                  }
                }}
              >
                {t('users.roles.menuChangeRole')}
              </Button>
            </Box>
          </Stack>

          <Stack
            gap={12}
            p={20}
            style={{
              flex: '1 1 320px',
              minWidth: 280,
              borderRadius: 12,
              border: '1px solid var(--color-border-default)',
              backgroundColor: 'var(--color-bg-surface)'
            }}
          >
            <Group justify="space-between" wrap="wrap" visibleFrom="sm">
              <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                {t('adminUserDetail.passwordTitle')}
              </Text>
              <Button
                size="xs"
                disabled={isSelf}
                onClick={() => setSendResetOpen(true)}
                styles={{
                  root: {
                    backgroundColor: 'var(--color-border-soft)',
                    color: 'var(--color-text-primary)'
                  }
                }}
              >
                {t('adminUserDetail.passwordButton')}
              </Button>
            </Group>
            <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }} hiddenFrom="sm">
              {t('adminUserDetail.passwordTitle')}
            </Text>
            <Text
              fz={{ base: 11.5, sm: 12 }}
              lh={{ base: '16px', sm: '18px' }}
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {isSelf
                ? t('adminUserDetail.selfActionNote')
                : t('adminUserDetail.passwordHelperText')}
            </Text>
            <Box hiddenFrom="sm">
              <Button
                size="xs"
                fullWidth
                disabled={isSelf}
                onClick={() => setSendResetOpen(true)}
                styles={{
                  root: {
                    backgroundColor: 'var(--color-border-soft)',
                    color: 'var(--color-text-primary)'
                  }
                }}
              >
                {t('adminUserDetail.passwordButton')}
              </Button>
            </Box>
          </Stack>
        </Group>

        <Stack
          gap={12}
          p={20}
          style={{
            width: '100%',
            borderRadius: 12,
            border: '1px solid var(--color-border-default)',
            backgroundColor: 'var(--color-bg-surface)'
          }}
        >
          <Group justify="space-between" wrap="wrap" visibleFrom="sm">
            <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
              {t('adminUserDetail.sessionSecurityTitle')}
            </Text>
            <Button
              size="xs"
              disabled={isSelf}
              onClick={() => setRevokeSessionModalOpen(true)}
              styles={{
                root: {
                  backgroundColor: 'var(--color-border-soft)',
                  color: 'var(--color-text-primary)'
                }
              }}
            >
              {t('adminUserDetail.sessionSecurityButton')}
            </Button>
          </Group>
          <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }} hiddenFrom="sm">
            {t('adminUserDetail.sessionSecurityTitle')}
          </Text>
          <Text
            fz={{ base: 11.5, sm: 12 }}
            lh={{ base: '16px', sm: '18px' }}
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {isSelf
              ? t('adminUserDetail.selfActionNote')
              : t('adminUserDetail.sessionSecurityHelperText')}
          </Text>
          <Box hiddenFrom="sm">
            <Button
              size="xs"
              disabled={isSelf}
              onClick={() => setRevokeSessionModalOpen(true)}
              styles={{
                root: {
                  backgroundColor: 'var(--color-border-soft)',
                  color: 'var(--color-text-primary)'
                }
              }}
            >
              {t('adminUserDetail.sessionSecurityButton')}
            </Button>
          </Box>
        </Stack>

        <Stack
          gap={12}
          p={20}
          style={{
            width: '100%',
            borderRadius: 12,
            border: '1px solid var(--color-border-default)',
            backgroundColor: 'var(--color-bg-surface)'
          }}
        >
          <Group justify="space-between" wrap="wrap" visibleFrom="sm">
            <Group gap={8} wrap="nowrap">
              <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                {t('adminUserDetail.accessStatusTitle')}
              </Text>
              <StatusPill
                label={userDetail.status}
                bg={
                  user.isActive
                    ? 'var(--color-status-success-soft)'
                    : 'var(--color-status-danger-soft)'
                }
                fg={user.isActive ? 'var(--color-status-success)' : 'var(--color-status-danger)'}
              />
            </Group>
            <Button
              size="xs"
              disabled={isSelf}
              onClick={() => setDeactivateModalOpen(true)}
              styles={{
                root: user.isActive
                  ? {
                      backgroundColor: 'var(--color-status-danger-solid)',
                      color: 'var(--color-text-on-secondary)'
                    }
                  : {}
              }}
            >
              {user.isActive
                ? t('adminUserDetail.deactivateButton')
                : t('adminUserDetail.reactivateButton')}
            </Button>
          </Group>
          <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }} hiddenFrom="sm">
            {t('adminUserDetail.accessStatusTitle')}
          </Text>
          <Text
            fz={{ base: 11.5, sm: 12.5 }}
            lh={{ base: '16px', sm: '18px' }}
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {isSelf
              ? t('adminUserDetail.selfActionNote')
              : user.isActive
                ? t('adminUserDetail.deactivateHelperText')
                : t('adminUserDetail.reactivateHelperText')}
          </Text>
          <Box hiddenFrom="sm">
            <Button
              size="xs"
              fullWidth
              disabled={isSelf}
              onClick={() => setDeactivateModalOpen(true)}
              styles={{
                root: user.isActive
                  ? {
                      backgroundColor: 'var(--color-status-danger-solid)',
                      color: 'var(--color-text-on-secondary)'
                    }
                  : {}
              }}
            >
              {user.isActive
                ? t('adminUserDetail.deactivateButton')
                : t('adminUserDetail.reactivateButton')}
            </Button>
          </Box>
        </Stack>
      </Stack>

      <ChangeRoleModal
        user={changeRoleOpen ? user : null}
        onClose={() => setChangeRoleOpen(false)}
        onConfirm={(userId, role) => {
          setActionFeedback(null);
          updateRoleMutation.mutate({ id: userId, data: { role } });
        }}
        loading={updateRoleMutation.isPending}
        isPaused={updateRoleMutation.isPaused}
      />

      <SendResetEmailModal
        user={sendResetOpen ? user : null}
        onClose={() => setSendResetOpen(false)}
        onConfirm={(userId) => {
          setActionFeedback(null);
          sendResetMutation.mutate({ id: userId });
        }}
        loading={sendResetMutation.isPending}
        isPaused={sendResetMutation.isPaused}
      />

      <ConfirmActionModal
        opened={deactivateModalOpen}
        onClose={() => setDeactivateModalOpen(false)}
        onConfirm={() => {
          setActionFeedback(null);
          if (user.isActive) {
            deactivateMutation.mutate({ id: user.id ?? '' });
          } else {
            reactivateMutation.mutate({ id: user.id ?? '' });
          }
        }}
        loading={deactivateMutation.isPending || reactivateMutation.isPending}
        isPaused={deactivateMutation.isPaused || reactivateMutation.isPaused}
        title={
          user.isActive
            ? t('adminUserDetail.deactivateModalTitle')
            : t('adminUserDetail.reactivateModalTitle')
        }
        contextLine={contextLine}
        message={
          user.isActive
            ? t('adminUserDetail.deactivateModalMessage')
            : t('adminUserDetail.reactivateModalMessage')
        }
        confirmLabel={
          user.isActive
            ? t('adminUserDetail.deactivateButton')
            : t('adminUserDetail.reactivateButton')
        }
        danger={user.isActive}
      />

      <ConfirmActionModal
        opened={revokeSessionModalOpen}
        onClose={() => setRevokeSessionModalOpen(false)}
        onConfirm={() => {
          setActionFeedback(null);
          revokeTokensMutation.mutate({ id: user.id ?? '' });
        }}
        loading={revokeTokensMutation.isPending}
        isPaused={revokeTokensMutation.isPaused}
        title={t('adminUserDetail.revokeSessionModalTitle')}
        contextLine={contextLine}
        message={t('adminUserDetail.revokeSessionModalMessage')}
        confirmLabel={t('adminUserDetail.revokeSessionConfirm')}
        danger
      />
    </Container>
  );
};
